/**
 * Organisation Users View Component
 *
 * Displays a full-width table of organisation members (human users).
 * Fetches data from Auth0OrgService (members),
 * enriches with UserEnrichmentService, and renders as a sortable PrimeNG table.
 *
 * @packageDocumentation
 */

import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { TableModule } from 'primeng/table';
import { SortEvent } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { FormsModule } from '@angular/forms';
import { FgaIconComponent } from '../fga-icon/fga-icon.component';
import { EllipsisTitleDirective } from '../../directives/ellipsis-title.directive';
import { Auth0OrganisationService } from '../../services/auth0-org.service';
import { FgaApiService } from '../../services/fga-api.service';
import { UserEnrichmentService } from '../../services/user-enrichment.service';
import { LoggerService } from '../../services/logger.service';
import { OrgUserStateService, OrgUserRow } from '../../services/org-user-state.service';
import { FGA_USER_PREFIX } from '../../models/fga-constants';
import { parseGroupId } from '../../models/group.model';
import { NgKitProgressSpinnerComponent } from '@wtw/angularkit/ngkit-progress-spinner';
import { ENTITY_TYPE } from '../../models/entity.model';
import { NgkitSearchComponent } from '@wtw/angularkit/ngkit-search';

const SERVICE_TAG = 'FgaOrgUsersView';

@Component({
  selector: 'fga-org-users-view',
  templateUrl: './fga-org-users-view.component.html',
  styleUrls: ['./fga-org-users-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TableModule,
    CheckboxModule,
    ButtonModule,
    TooltipModule,
    DialogModule,
    DrawerModule,
    FormsModule,
    FgaIconComponent,
    EllipsisTitleDirective,
    NgKitProgressSpinnerComponent,
    NgkitSearchComponent,
  ],
  providers: [OrgUserStateService],
})
export class FgaOrgUsersViewComponent implements OnChanges {
  @Input() organisationId?: string;
  @Input() organisationName?: string;

  private readonly auth0OrgService = inject(Auth0OrganisationService);
  private readonly fgaApiService = inject(FgaApiService);
  private readonly enrichmentService = inject(UserEnrichmentService);
  private readonly logger = inject(LoggerService);
  readonly state = inject(OrgUserStateService);

  readonly selectedIds = signal<Set<string>>(new Set());

  readonly selectAllActive = signal<boolean>(false);

  readonly confirmDialogOpen = signal<boolean>(false);

  readonly searchQuery = signal<string>('');

  readonly selectedRowId = signal<string | null>(null);

  readonly groupsFilterQuery = signal<string>('');

  readonly userGroups = signal<string[]>([]);

  readonly groupsLoading = signal<boolean>(false);

  readonly groupsError = signal<string | null>(null);

  readonly filteredUsers = computed<OrgUserRow[]>(() => {
    const term = this.searchQuery().trim().toLowerCase();
    const users = this.state.users();

    if (!term) return [...users];

    return users.filter(
      user =>
        user.displayName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
    );
  });

  readonly hasSearchResults = computed(() => this.filteredUsers().length > 0);

  readonly selectedRow = computed<OrgUserRow | null>(() => {
    const selectedId = this.selectedRowId();

    if (!selectedId) return null;

    return this.state.users().find(({ id }) => id === selectedId) ?? null;
  });

  readonly selectedUserGroups = computed<string[]>(() => {
    const selected = this.selectedRow();

    if (!selected) return [];

    const sourceGroups = this.userGroups();
    const normalizedFilter = this.groupsFilterQuery().trim().toLowerCase();
    const sortedGroups = [...sourceGroups]
      .map(groupId => this.toGroupDisplayName(groupId))
      .sort((a, b) => a.localeCompare(b));

    if (!normalizedFilter) return sortedGroups;

    return sortedGroups.filter(group => group.toLowerCase().includes(normalizedFilter));
  });

  readonly selectedUserRows = computed<OrgUserRow[]>(() => {
    const selected = this.selectedIds();
    const rows = this.selectAllActive() ? this.state.users() : this.filteredUsers();

    return rows.filter(({ id }) => selected.has(id));
  });

  readonly rowPendingRemoval = signal<OrgUserRow | null>(null);

  readonly removalTargets = computed<OrgUserRow[]>(() => {
    const pending = this.rowPendingRemoval();

    return pending ? [pending] : this.selectedUserRows();
  });

  readonly allSelected = computed(() => this.selectAllActive());

  readonly someSelected = computed(
    () => !this.selectAllActive() && this.selectedIds().size > 0
  );

  readonly selectionCount = computed(() => this.selectedUserRows().length);

  readonly removeButtonLabel = computed(() => {
    const count = this.selectionCount();
    return count > 0 ? `Remove (${count})` : 'Remove';
  });

  readonly removeButtonDisabled = computed(() => this.selectionCount() === 0 || this.state.removing());

  readonly showRemoveButton = computed(() => this.selectionCount() > 0);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organisationId']?.currentValue) {
      this.loadUsers();
    }
  }

  async loadUsers(): Promise<void> {
    if (!this.organisationId) return;

    this.state.setLoading(true);
    this.state.setError(null);
    this.selectedIds.set(new Set());
    this.selectAllActive.set(false);
    this.searchQuery.set('');
    this.selectedRowId.set(null);
    this.groupsFilterQuery.set('');
    this.userGroups.set([]);
    this.groupsLoading.set(false);
    this.groupsError.set(null);

    try {
      const rows = await this.loadOrgMembers();

      this.state.setUsers(rows);
      this.logger.debug(SERVICE_TAG, `Loaded ${rows.length} users`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load users';

      this.state.setError(message);
      this.logger.error(SERVICE_TAG, 'Failed to load org users', { error: message });
    } finally {
      this.state.setLoading(false);
    }
  }

  private async loadOrgMembers(): Promise<OrgUserRow[]> {
    if (!this.organisationId || !this.auth0OrgService.configured()) {
      return [];
    }

    try {
      const members = await this.auth0OrgService.loadMembersByOrgId(this.organisationId);
      const rows: OrgUserRow[] = members.map(member => ({
        id: `user:${member.user_id}`,
        displayName: member.name || member.email || member.user_id,
        email: member.email || '',
        type: ENTITY_TYPE.USER,
      }));
      const fgaUserIds = rows.map(row => row.id);

      this.enrichInBackground(fgaUserIds);

      return rows;
    } catch (err) {
      this.logger.warn(
        SERVICE_TAG,
        'Failed to load org members, falling back to empty list',
        { error: err instanceof Error ? err.message : String(err) }
      );
      throw err;
    }
  }

  private async enrichInBackground(fgaUserIds: string[]): Promise<void> {
    if (!this.enrichmentService.enabled()) return;

    try {
      const enriched = await this.enrichmentService.enrichUsers(fgaUserIds);

      if (!enriched?.length) return;

      const enrichedMap = new Map(enriched.map(item => [item.fgaUserId, item]));
      const updatedUsers = this.state.users().map(row => {
        const data = enrichedMap.get(row.id);

        if (data) {
          return {
            ...row,
            displayName: data.name || data.displayName || row.displayName,
            email: data.email || row.email,
          };
        }
        return row;
      });
      this.state.setUsers(updatedUsers);
    } catch (err) {
      this.logger.warn(
        SERVICE_TAG,
        'Background enrichment failed',
        { error: err instanceof Error ? err.message : String(err) }
      );
    }
  }

  toggleRow(id: string): void {
    const row = this.state.users().find(user => user.id === id);

    if (!row) return;

    this.selectAllActive.set(false);

    this.selectedIds.update(set => {
      const next = new Set(set);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  async selectRow(row: OrgUserRow): Promise<void> {
    this.selectedRowId.set(row.id);
    this.groupsFilterQuery.set('');
    this.userGroups.set([]);
    this.groupsError.set(null);

    this.groupsLoading.set(true);

    try {
      const [memberGroups, adminGroups] = await Promise.all([
        firstValueFrom(this.fgaApiService.getUserGroupsByRelation(row.id, 'member')),
        firstValueFrom(this.fgaApiService.getUserGroupsByRelation(row.id, 'admin')),
      ]);

      // Ignore stale responses if the user changed selection during load.
      if (this.selectedRowId() !== row.id) return;

      const mergedGroups = Array.from(new Set([...memberGroups.groups, ...adminGroups.groups])).sort();
      this.userGroups.set(mergedGroups);
    } catch (err) {
      if (this.selectedRowId() !== row.id) return;

      const message = err instanceof Error ? err.message : 'Failed to load user groups';
      this.groupsError.set(message);
      this.logger.error(SERVICE_TAG, 'Failed to load groups for selected user', {
        userId: row.id,
        error: message,
      });
    } finally {
      if (this.selectedRowId() === row.id) {
        this.groupsLoading.set(false);
      }
    }
  }

  clearGroupsFilter(): void {
    this.groupsFilterQuery.set('');
  }

  closeDetailsPanel(): void {
    this.selectedRowId.set(null);
    this.groupsFilterQuery.set('');
    this.userGroups.set([]);
    this.groupsLoading.set(false);
    this.groupsError.set(null);
  }

  private toGroupDisplayName(groupId: string): string {
    try {
      return parseGroupId(groupId).name;
    } catch {
      return groupId;
    }
  }

  toggleAll(): void {
    if (this.selectAllActive()) {
      this.selectAllActive.set(false);
      this.selectedIds.set(new Set());
      return;
    }

    const all = this.state.users();

    if (all.length === 0) return;

    this.selectAllActive.set(true);
    this.selectedIds.set(new Set(all.map(({ id }) => id)));
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  customSort(event: SortEvent): void {
    const data = (event.data ?? []) as OrgUserRow[];
    const field = event.field ?? 'displayName';
    const order = event.order ?? 1;

    data.sort((a, b) => {
      const valueA = String((a as unknown as Record<string, unknown>)[field] ?? '');
      const valueB = String((b as unknown as Record<string, unknown>)[field] ?? '');

      return order * valueA.localeCompare(valueB);
    });
  }

  onSearch(value: string): void {
    this.searchQuery.set(value ?? '');
  }

  clearSearch(): void {
    this.searchQuery.set('');
  }

  openRemoveDialog(): void {
    if (this.selectionCount() === 0) return;

    this.openConfirmDialog(null);
  }

  openRowRemoveDialog(row: OrgUserRow): void {
    this.openConfirmDialog(row);
  }

  private openConfirmDialog(target: OrgUserRow | null): void {
    if (this.state.removing()) return;

    this.rowPendingRemoval.set(target);
    this.state.setRemoveError(null);
    this.state.setRemoveSuccess(null);
    this.confirmDialogOpen.set(true);
  }

  cancelRemove(): void {
    if (this.state.removing()) return;

    this.confirmDialogOpen.set(false);
    this.rowPendingRemoval.set(null);
    this.state.setRemoveError(null);
  }

  async removeSelectedUsers(): Promise<void> {
    const rowsToRemove = this.removalTargets();

    if (rowsToRemove.length === 0 || this.state.removing()) return;

    this.state.setRemoving(true);
    this.state.setRemoveError(null);
    this.state.setRemoveSuccess(null);

    const removedIds = new Set(rowsToRemove.map(({ id }) => id));
    const userIds = [...removedIds]
      .filter(id => id.startsWith(FGA_USER_PREFIX))
      .map(id => id.slice(FGA_USER_PREFIX.length));

    try {
      await Promise.all(
        userIds.map(userId => firstValueFrom(this.fgaApiService.removeOrgMember(userId)))
      );

      if (userIds.length > 0) {
        await this.auth0OrgService.removeMembers(userIds);
      }

      this.state.removeUsersByIds(removedIds);
      this.selectedIds.update(set => {
        const next = new Set(set);
        removedIds.forEach(id => next.delete(id));
        return next;
      });
      this.selectAllActive.set(false);
      this.rowPendingRemoval.set(null);
      this.confirmDialogOpen.set(false);
      this.state.setRemoveSuccess(
        `Removed ${userIds.length} ${userIds.length === 1 ? 'user' : 'users'} from the organisation.`
      );

      this.logger.info(SERVICE_TAG, `Removed ${userIds.length} members successfully`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove users';

      this.state.setRemoveError(message);
      this.logger.error(SERVICE_TAG, 'Failed to remove members', { error: message });
    } finally {
      this.state.setRemoving(false);
    }
  }

  dismissSuccess(): void {
    this.state.setRemoveSuccess(null);
  }
}
